/**
 * Appointment Card Component
 * Display appointment in list or grid view
 */

import type { AppointmentWithDetails } from '../../hooks/useAppointments';
import {
  formatTime,
  formatDuration,
  getStatusColor,
  getStatusLabel,
} from '../../utils/calendar';

interface AppointmentCardProps {
  appointment: AppointmentWithDetails;
  onClick: () => void;
}

export function AppointmentCard({ appointment, onClick }: AppointmentCardProps) {
  const statusColors = getStatusColor(appointment.status);
  const startTime = formatTime(appointment.appointment_datetime);
  const endDate = new Date(appointment.appointment_datetime);
  endDate.setMinutes(endDate.getMinutes() + appointment.duration_minutes);
  const endTime = formatTime(endDate);

  return (
    <div
      className={`border-l-4 ${statusColors.border} bg-white rounded-lg shadow-sm p-4 cursor-pointer transition-shadow hover:shadow-md`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          {/* Time */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-medium text-gray-900">
              {startTime} - {endTime}
            </span>
            <span className="text-xs text-gray-500">
              ({formatDuration(appointment.duration_minutes)})
            </span>
          </div>

          {/* Patient */}
          <div className="mb-1">
            <span className="text-base font-semibold text-gray-900">
              {appointment.patient_name || 'Óþekktur sjúklingur'}
            </span>
          </div>

          {/* Appointment Type */}
          <div className="mb-2">
            <span className="text-sm text-gray-600">{appointment.appointment_type}</span>
          </div>

          {/* Provider */}
          {appointment.provider_name && (
            <div className="text-sm text-gray-500">
              <span className="font-medium">Meðferðaraðili:</span> {appointment.provider_name}
            </div>
          )}

          {/* Reason */}
          {appointment.reason && (
            <div className="mt-2 text-sm text-gray-600">{appointment.reason}</div>
          )}
        </div>

        {/* Status Badge */}
        <div className="flex-shrink-0 ml-4">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors.bg} ${statusColors.text}`}
          >
            {getStatusLabel(appointment.status)}
          </span>
        </div>
      </div>

      {/* Reminder Indicators */}
      {(appointment.reminder_sent_24h || appointment.reminder_sent_2h) && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-3 text-xs text-gray-500">
            {appointment.reminder_sent_24h && (
              <div className="flex items-center gap-1">
                <svg
                  className="w-4 h-4 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>24h áminning send</span>
              </div>
            )}
            {appointment.reminder_sent_2h && (
              <div className="flex items-center gap-1">
                <svg
                  className="w-4 h-4 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>2h áminning send</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
